export const fetchPitchData = async (gamePk) => {
  try {
    const [pbpRes, linescoreRes] = await Promise.all([
      fetch(`https://statsapi.mlb.com/api/v1/game/${gamePk}/playByPlay`),
      fetch(`https://statsapi.mlb.com/api/v1/game/${gamePk}/linescore`),
    ]);
    const playByPlay = pbpRes.ok ? await pbpRes.json() : null;
    const linescore = linescoreRes.ok ? await linescoreRes.json() : null;
    return { playByPlay, linescore };
  } catch {
    return { playByPlay: null, linescore: null };
  }
};

export const parseCurrentAtBat = (allPlays = []) => {
  if (!allPlays.length) return null;
  return allPlays[allPlays.length - 1];
};

export const parsePitchLog = (allPlays = [], limit = 30) => {
  const pitches = [];
  allPlays.forEach((play, playIdx) => {
    const batter = play.matchup?.batter?.fullName || '--';
    const pitcher = play.matchup?.pitcher?.fullName || '--';
    const inning = play.about?.inning;
    const isTop = play.about?.isTopInning;
    (play.playEvents || []).forEach((ev) => {
      if (!ev.isPitch) return;
      pitches.push({
        playIdx,
        batter,
        pitcher,
        inning,
        isTop,
        type: ev.details?.type?.description || ev.details?.description || '--',
        code: ev.details?.code || '--',
        call: ev.details?.call?.description || '--',
        speed: ev.pitchData?.startSpeed ? Math.round(ev.pitchData.startSpeed) : null,
        spinRate: ev.pitchData?.breaks?.spinRate ? Math.round(ev.pitchData.breaks.spinRate) : null,
        zone: ev.pitchData?.zone || null,
        count: ev.count || null,
        description: ev.details?.description || '--',
        isStrike: ['S', 'C', 'F', 'T', 'K', 'L', 'O', 'Q'].includes(ev.details?.code),
        isBall: ['B', 'P', 'I'].includes(ev.details?.code),
        isHit: ['D', 'E', 'H', 'X'].includes(ev.details?.code),
        isHR: ev.details?.code === 'H',
        result: ev.details?.call?.description || ev.details?.description || '',
      });
    });
  });
  return pitches.slice(-limit).reverse();
};

export const parseBoxScore = (boxScore) => {
  if (!boxScore) return { away: [], home: [], awayPitchers: [], homePitchers: [] };

  const extractBatters = (side) => {
    const players = Object.values(boxScore.teams?.[side]?.players || {});
    const battingOrder = boxScore.teams?.[side]?.battingOrder || [];
    const ordered = battingOrder
      .map(id => players.find(p => p.person?.id === id))
      .filter(Boolean);
    const rest = players.filter(
      p => p.stats?.batting?.atBats > 0 && !battingOrder.includes(p.person?.id)
    );
    return [...ordered, ...rest]
      .filter(p => p?.stats?.batting)
      .map(p => ({
        id: p.person?.id,
        name: p.person?.fullName || '--',
        pos: p.position?.abbreviation || '--',
        battingOrder: p.battingOrder,
        ab: p.stats.batting.atBats ?? 0,
        r: p.stats.batting.runs ?? 0,
        h: p.stats.batting.hits ?? 0,
        rbi: p.stats.batting.rbi ?? 0,
        bb: p.stats.batting.baseOnBalls ?? 0,
        k: p.stats.batting.strikeOuts ?? 0,
        hr: p.stats.batting.homeRuns ?? 0,
        lob: p.stats.batting.leftOnBase ?? 0,
        avg: p.stats.batting.atBats > 0
          ? (p.stats.batting.hits / p.stats.batting.atBats).toFixed(3).replace('0.', '.')
          : '.000',
      }));
  };

  const extractPitchers = (side) => {
    const players = Object.values(boxScore.teams?.[side]?.players || {});
    return players
      .filter(p => p.stats?.pitching && parseFloat(p.stats.pitching.inningsPitched) > 0)
      .sort((a, b) =>
        parseFloat(b.stats.pitching.inningsPitched) - parseFloat(a.stats.pitching.inningsPitched)
      )
      .map(p => ({
        id: p.person?.id,
        name: p.person?.fullName || '--',
        ip: p.stats.pitching.inningsPitched ?? '0.0',
        h: p.stats.pitching.hits ?? 0,
        r: p.stats.pitching.runs ?? 0,
        er: p.stats.pitching.earnedRuns ?? 0,
        bb: p.stats.pitching.baseOnBalls ?? 0,
        k: p.stats.pitching.strikeOuts ?? 0,
        hr: p.stats.pitching.homeRuns ?? 0,
        p: p.stats.pitching.pitchesThrown ?? '--',
        s: p.stats.pitching.strikes ?? '--',
        era: p.stats.pitching.era ?? '--',
      }));
  };

  return {
    away: extractBatters('away'),
    home: extractBatters('home'),
    awayPitchers: extractPitchers('away'),
    homePitchers: extractPitchers('home'),
    awayTeamStats: boxScore.teams?.away?.teamStats?.batting,
    homeTeamStats: boxScore.teams?.home?.teamStats?.batting,
  };
};